import { memo, useMemo } from "react";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { View, Text, Button, Icon } from "@visioni/a3s-ui/components";
import { AccessCard } from "../AccessCard";
import { SiteAccessList, type SiteAccessListSite } from "../SiteAccessList";
import { CameraAccessList, type CameraAccessListCamera } from "../CameraAccessList";

export type EditUserFormData = {
  tenantAdmin: boolean;
  sites: Map<string, "manager" | "viewer">;
  cameras: Map<string, "manager" | "viewer">;
};

export type EditUserFormProps = {
  userName: string;
  userEmail: string;
  tenantAdmin: boolean;
  sites: SiteAccessListSite[];
  cameras: CameraAccessListCamera[];
  siteSelection: Map<string, "manager" | "viewer">;
  cameraSelection: Map<string, "manager" | "viewer">;
  /** Whether the form submission is in progress. Disables the submit button. */
  isPending?: boolean;
  onTenantAdminToggle: () => void;
  onSiteToggle: (id: string) => void;
  onSiteRoleChange: (id: string, role: "manager" | "viewer") => void;
  onCameraToggle: (id: string) => void;
  onCameraRoleChange: (id: string, role: "manager" | "viewer") => void;
  onSubmit: () => void;
  onCancel: () => void;
  onRemoveAll?: () => void;
};

const TENANT_ADMIN_ID = "tenant_admin";

export const EditUserForm = memo(
  ({
    userName,
    userEmail,
    tenantAdmin,
    sites,
    cameras,
    siteSelection,
    cameraSelection,
    isPending = false,
    onTenantAdminToggle,
    onSiteToggle,
    onSiteRoleChange,
    onCameraToggle,
    onCameraRoleChange,
    onSubmit,
    onCancel,
    onRemoveAll,
  }: EditUserFormProps) => {
    const hasLegacyCameras = useMemo(() => cameras.some((c) => c.legacy), [cameras]);

    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.userInfo}>
            <Text textStyle="lg/semibold">{userName}</Text>
            <Text textStyle="sm/regular" color="textMuted">
              {userEmail}
            </Text>
          </View>
          <View style={styles.headerActions}>
            {onRemoveAll && (
              <Button
                label="Remove All"
                variant="secondary"
                variantColor="red"
                onPress={onRemoveAll}
                loading={isPending}
              />
            )}
            <Button label="Cancel" variant="secondary" onPress={onCancel} disabled={isPending} />
            <Button label="Submit" variant="primary" onPress={onSubmit} loading={isPending} />
          </View>
        </View>

        {hasLegacyCameras && <LegacyWarningBanner />}

        <AccessCard
          id={TENANT_ADMIN_ID}
          name="Tenant Admin"
          subtitle="Full access to all sites and cameras"
          showThumbnail={false}
          showRoleControl={false}
          selected={tenantAdmin}
          role="manager"
          onToggle={onTenantAdminToggle}
          onRoleChange={onTenantAdminToggle}
        />

        <SiteAccessList
          sites={sites}
          selected={siteSelection}
          onToggle={onSiteToggle}
          onRoleChange={onSiteRoleChange}
        />

        <CameraAccessList
          cameras={cameras}
          selected={cameraSelection}
          onToggle={onCameraToggle}
          onRoleChange={onCameraRoleChange}
        />
      </View>
    );
  },
);

const LegacyWarningBanner = memo(() => {
  return (
    <View style={styles.warningBanner}>
      <Icon name="warning" size={18} color="#92400e" />
      <Text textStyle="sm/regular" style={styles.warningText}>
        Legacy camera permissions cannot be edited here. Contact Vision Support for help managing permissions on those
        cameras.
      </Text>
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.xl,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  userInfo: {
    flex: 1,
    gap: 2,
  },
  headerActions: {
    flexDirection: "row",
    gap: theme.spacing.md,
  },
  warningBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    padding: theme.spacing.lg,
    backgroundColor: "#fef3c7",
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: "#f59e0b",
  },
  warningText: {
    flex: 1,
    color: "#92400e",
  },
}));
